import { RemoveButton } from '@/components/Elements/Button'
import {
  DateCell,
  DatePickerCell,
  InputCell,
  NumberInputCell,
  SelectCell,
  TextareaCell,
} from '@/components/Table/components/Cell'
import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'
import { ISO_3166_ALPHA_2_CODES } from '@/constants/countryCodes'
import { camelToWords } from '@/utils/stringHelper'
import { Button } from '@chakra-ui/react'
import {
  getQuotationDetailTableColumnIds,
  OfferQuotationDetailInstallAndSupplyUnit,
  OfferQuotationDetailMaintenanceUnit,
  OfferQuotationTemplateScopeOfWork,
} from '@eapproval/offer-shared'
import { createColumnHelper } from '@tanstack/react-table'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
const columnHelper = createColumnHelper()

export const columnIdToDetail = ({ id, template = [], onCellPaste, ...rest }) => {
  const { t } = useTranslation()

  switch (id) {
    case 'action':
      return columnHelper.display({
        id: 'action',
        size: 50,
        enableHiding: false,
        enableColumnFilter: false,
        cell: ({ getValue, row: { original, index }, table, extraDisable = false }) => {
          const value = getValue()
          const isDisabled = table.options.meta?.disabled || extraDisable
          const isTemplateRow = index < template.length

          if (isDisabled || isTemplateRow) {
            return <></>
          }

          return <RemoveButton onClick={() => rest.handleRemove(index)} />
        },
      })
    case 'scopeOfWorkSelect':
      return columnHelper.accessor('scopeOfWork', {
        size: 300,
        header: () => <span>Scope of Work</span>,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return (
            <SelectCell
              {...cell}
              options={Object.values(OfferQuotationTemplateScopeOfWork).map((val) => {
                return {
                  value: val,
                  label: camelToWords(val),
                }
              })}
            />
          )
        },
      })

    case 'modelNumber':
      return columnHelper.accessor('modelNumber', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_MODEL_NUMBER')}</span>,
        size: 100,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <TextareaCell {...cell} handlePaste={onCellPaste} />
        },
      })
    case 'productDescription':
      return columnHelper.accessor('productDescription', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_DESCRIPTION')}</span>,
        size: 120,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <TextareaCell {...cell} handlePaste={onCellPaste} />
        },
      })
    case 'description':
      return columnHelper.accessor('description', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_DESCRIPTION')}</span>,
        size: 150,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <TextareaCell {...cell} handlePaste={onCellPaste} />
        },
      })
    case 'brand':
      return columnHelper.accessor('brand', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_BRAND')}</span>,
        size: 150,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <TextareaCell {...cell} handlePaste={onCellPaste} />
        },
      })
    case 'quantity':
      return columnHelper.accessor('quantity', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_QUANTITY')}</span>,
        size: 100,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <NumberInputCell {...cell} handlePaste={onCellPaste} />
        },
      })
    case 'unit':
      return columnHelper.accessor('unit', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_UNIT')}</span>,
        size: 90,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <InputCell {...cell} handlePaste={onCellPaste} />
        },
      })
    case 'unitSelect':
      return columnHelper.accessor('unit', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_UNIT')}</span>,
        size: 150,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }

          return (
            <SelectCell
              {...cell}
              options={Object.values(OfferQuotationDetailInstallAndSupplyUnit).map((val) => {
                return {
                  value: val,
                  label: val,
                }
              })}
            />
          )
        },
      })
    case 'unitMaintenance':
      return columnHelper.accessor('unit', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_UNIT')}</span>,
        size: 100,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }

          return (
            <SelectCell
              {...cell}
              options={Object.values(OfferQuotationDetailMaintenanceUnit).map((val) => {
                return {
                  value: val,
                  label: val,
                }
              })}
            />
          )
        },
      })
    case 'unitRate':
      return columnHelper.accessor('unitRate', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_UNIT_RATE')}</span>,
        size: 100,

        cell: (cell) => <NumberInputCell {...cell} type={'number'} handlePaste={onCellPaste} decimalScale={2} />,
      })
    case 'discount':
      return columnHelper.accessor('discount', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_DISCOUNT_PERCENT')}</span>,
        size: 100,

        cell: (cell) => <NumberInputCell {...cell} type={'number'} handlePaste={onCellPaste} decimalScale={2} />,
      })
    case 'totalPrice':
      return columnHelper.accessor('totalPrice', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_TOTAL_PRICE')}</span>,
        size: 100,

        cell: (cell) => <TooltipCell text={`$${cell.getValue() ?? 0}`} />,
      })
    case 'country':
      return columnHelper.accessor('country', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_COUNTRY')}</span>,
        size: 200,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <SelectCell {...cell} options={ISO_3166_ALPHA_2_CODES} />
        },
      })
    case 'category':
      return columnHelper.accessor('category', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_CATEGORY')}</span>,
        size: 300,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <SelectCell {...cell} options={rest?.categoryOptions ?? []} extraDisable={rest?.disableCategory} />
        },
      })
    case 'AL':
      return columnHelper.accessor('AL', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_AL')}</span>,
        size: 200,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <SelectCell {...cell} options={rest?.alOptions ?? []} />
        },
      })
    case 'ECCN':
      return columnHelper.accessor('ECCN', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_ECCN')}</span>,
        size: 200,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <SelectCell {...cell} options={rest?.eccnOptions ?? []} />
        },
      })
    case 'HSCode':
      return columnHelper.accessor('HSCode', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_HSCODE')}</span>,
        size: 200,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <SelectCell {...cell} options={rest?.hsCodeOptions ?? []} />
        },
      })
    case 'ECCNDetail':
      return columnHelper.accessor('ECCNDetail', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_ECCN_DETAIL')}</span>,
        size: 100,

        cell: ({ row: { index, original } }) => {
          if (rest.handleOpenECCNDetailModel && (original.ECCN || original.ECCN === '')) {
            return (
              <Button onClick={() => rest.handleOpenECCNDetailModel(original.ECCNDetail ?? [], index)}>
                {t('VENDOR_UPLOAD_PAGE_DATA_TABLE_ECCN_DETAIL')}
              </Button>
            )
          }
          return <></>
        },
      })

    case 'startDate':
      return columnHelper.accessor('startDate', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_START_DATE')}</span>,
        size: 100,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <DateCell {...cell} />
          }

          return <DatePickerCell {...cell} />
        },
      })
    case 'endDate':
      return columnHelper.accessor('endDate', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_END_DATE')}</span>,
        size: 100,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <DateCell {...cell} />
          }
          return <DatePickerCell {...cell} />
        },
      })
    case 'siteLocation':
      return columnHelper.accessor('siteLocation', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_SITE_LOCATION')}</span>,
        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <TextareaCell {...cell} handlePaste={onCellPaste} />
        },
      })
    case 'numberOfWorker':
      return columnHelper.accessor('numberOfWorker', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_NO_OF_WORKER_ON_SITE')}</span>,
        size: 100,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <TextareaCell {...cell} handlePaste={onCellPaste} />
        },
      })
    case 'system':
      return columnHelper.accessor('system', {
        header: () => <span>{t('VENDOR_UPLOAD_PAGE_DATA_TABLE_SYSTEM')}</span>,
        size: 100,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <TextareaCell {...cell} handlePaste={onCellPaste} />
        },
      })
    case 'SAPMaterialNumber':
      return columnHelper.accessor('SAPMaterialNumber', {
        header: () => <span>SAP #</span>,
        size: 100,
      })
    default:
      return columnHelper.accessor(id, {
        size: 100,

        cell: (cell) => {
          const rowInTemplate = cell.row.index < template.length ? template[cell.row.index] : undefined
          if (rowInTemplate && rowInTemplate[cell.column.id] && rowInTemplate[cell.column.id] !== '') {
            return <span>{cell.getValue()}</span>
          }
          return <TextareaCell {...cell} handlePaste={onCellPaste} />
        },
      })
  }
}

export const getQuotationDetailExcelColumnIds = (costBudgetType) => {
  let columnIds = getQuotationDetailTableColumnIds({
    costBudgetType,
    canRemove: false,
    isExcel: true,
    isTemplate: false,
  })
  return columnIds
    .filter((id) => id !== 'action' && id !== 'ECCNDetail')
    .map((id) => {
      if (id === 'scopeOfWorkSelect') {
        return 'scopeOfWork'
      } else if (id === 'unitSelect') {
        return 'unit'
      } else if (id === 'unitMaintenance') {
        return 'unit'
      } else {
        return id
      }
    })
}

export const getQuotationDetailTableColumns = ({ costBudgetType, ...rest }) => {
  const columnIds = getQuotationDetailTableColumnIds({
    costBudgetType,
    canRemove: true,
    isExcel: false,
    isTemplate: false,
  })

  return columnIds.map((id) => columnIdToDetail({ id, ...rest }))
}

/*
  Input data: Array of Object
  @Output: void
  Validate the imported data from excel before setting to the form
*/

export const validateImportedData = (data, template = []) => {
  //User can only add row after the template content
  if (template.length !== 0 && data.length < template.length) {
    throw new Error(`Imported data has less rows than the template. Please check your Excel file.`)
  }
  data.forEach((dataRow, rowIndex) => {
    //Check if there is empty value
    const objectEntries = Object.entries(dataRow)
    // objectEntries.forEach(([key, value]) => {
    //   if (value === null || value === undefined || value === '') {
    //     throw new Error(`Missing data in Excel file row ${rowIndex + 1}  - Missing value for ${camelToWords(key)}`)
    //   }
    // })

    //Check if the total price is correct
    const quantity = parseFloat(dataRow['quantity']) || 0
    const unitRate = parseFloat(dataRow['unitRate']) || 0
    const discount = parseFloat(dataRow['discount']) || 0

    const calculatedTotalPrice = quantity * unitRate * (1 - discount / 100)
    const totalPrice = parseFloat(dataRow['totalPrice']) || 0

    if (Math.abs(calculatedTotalPrice - totalPrice) > 0.01) {
      throw new Error(
        `Total Price mismatch in Excel file row ${rowIndex + 1} - Expected: $${calculatedTotalPrice.toFixed(
          2
        )}, Found: $${totalPrice.toFixed(2)}`
      )
    }
    //Validate Against the template if provided

    if (rowIndex >= template.length) {
      //This is a new row created by user, skip template validation
      return
    }

    const templateRow = template[rowIndex]

    if (!templateRow) {
      throw new Error(`Invalid Scope of Work in Excel file row ${rowIndex + 1} - ${dataRow.scopeOfWork}`)
    }

    //Check if the content in template has been changed
    Object.entries(dataRow).forEach(([key, value]) => {
      const templateValue = templateRow[key]
      if (templateValue === '' || templateValue === null || templateValue === undefined) {
        return
      }

      //Skip checking the quantity row
      if (key === 'ECCNDetail') {
        return
      }

      if (key.includes('Date')) {
        if (!moment(templateValue).isSame(moment(value))) {
          throw new Error(
            `Mismatch date in Excel file row ${rowIndex + 1} - Expected: ${camelToWords(
              key
            )} to be "${templateValue}", Found: "${value}"`
          )
        }
        //COmpate the
        return
      }

      if (templateValue != value) {
        throw new Error(
          `Mismatch data in Excel file row ${rowIndex + 1} - Expected: ${camelToWords(
            key
          )} to be "${templateValue}", Found: "${value}"`
        )
      }
    })
  })
}
